import { PURCHASE_ORDER_KINDS, type PurchaseOrderKind } from "@threadheads/shared";

export type PurchaseOrderSortField =
  | "po_number"
  | "supplier_name"
  | "status"
  | "submitted_date"
  | "created_at"
  | "total_cost";

export type PurchaseOrderSortOrder = "asc" | "desc";

export type PurchaseOrderStatusFilter =
  | "all"
  | "draft"
  | "submitted"
  | "in_production"
  | "ready"
  | "in_transit"
  | "partially_received"
  | "received"
  | "cancelled";

/** BLU-1268: purchase order kinds ("all" = every kind, omitted from the URL). */
export type PurchaseOrderKindFilter = "all" | PurchaseOrderKind;

/** BLU-1268: the PO list's views ("orders" is the default and omitted from the URL). */
export type PurchaseOrderListTab = "orders" | "to-buy";

export interface PurchaseOrderListState {
  tab: PurchaseOrderListTab;
  statusFilter: PurchaseOrderStatusFilter;
  kindFilter: PurchaseOrderKindFilter;
  supplierFilter: string;
  activeOnly: boolean;
  search: string;
  sortBy: PurchaseOrderSortField;
  sortOrder: PurchaseOrderSortOrder;
  page: number;
  /** To buy's location (`?loc=`); null = the switcher's, else the first owned floor. */
  toBuyLocationId: string | null;
}

const KIND_FILTERS = new Set<PurchaseOrderKindFilter>(["all", ...PURCHASE_ORDER_KINDS]);

const STATUS_FILTERS = new Set<PurchaseOrderStatusFilter>([
  "all",
  "draft",
  "submitted",
  "in_production",
  "ready",
  "in_transit",
  "partially_received",
  "received",
  "cancelled",
]);

const SORT_FIELDS = new Set<PurchaseOrderSortField>([
  "po_number",
  "supplier_name",
  "status",
  "submitted_date",
  "created_at",
  "total_cost",
]);

export const parsePurchaseOrderListSearch = (search: string): PurchaseOrderListState => {
  const params = new URLSearchParams(search);
  const rawStatus = params.get("status") as PurchaseOrderStatusFilter | null;
  const statusFilter = rawStatus && STATUS_FILTERS.has(rawStatus) ? rawStatus : "all";
  const rawSort = params.get("sort") as PurchaseOrderSortField | null;
  const sortBy = rawSort && SORT_FIELDS.has(rawSort) ? rawSort : "created_at";
  const rawPage = Number.parseInt(params.get("page") ?? "1", 10);
  const rawKind = params.get("kind") as PurchaseOrderKindFilter | null;
  const tab: PurchaseOrderListTab = params.get("tab") === "to-buy" ? "to-buy" : "orders";

  return {
    tab,
    statusFilter,
    kindFilter: rawKind && KIND_FILTERS.has(rawKind) ? rawKind : "all",
    supplierFilter: params.get("supplier") || "all",
    activeOnly: statusFilter === "all" && params.get("active") !== "0",
    search: params.get("q") ?? "",
    sortBy,
    sortOrder: params.get("direction") === "asc" ? "asc" : "desc",
    page: Number.isFinite(rawPage) && rawPage > 0 ? rawPage : 1,
    toBuyLocationId: tab === "to-buy" ? params.get("loc") || null : null,
  };
};

export const buildPurchaseOrderListSearch = (state: PurchaseOrderListState): string => {
  const params = new URLSearchParams();
  // The view first, so a To buy link reads `?tab=to-buy` at a glance. Without
  // it the URL sync effect would strip the tab on the first render (BLU-1268).
  if (state.tab === "to-buy") {
    params.set("tab", "to-buy");
    if (state.toBuyLocationId) params.set("loc", state.toBuyLocationId);
  }
  if (state.search) params.set("q", state.search);
  if (state.statusFilter !== "all") params.set("status", state.statusFilter);
  if (state.supplierFilter !== "all") params.set("supplier", state.supplierFilter);
  if (state.kindFilter !== "all") params.set("kind", state.kindFilter);
  if (state.statusFilter === "all" && !state.activeOnly) params.set("active", "0");
  if (state.sortBy !== "created_at") params.set("sort", state.sortBy);
  if (state.sortOrder !== "desc") params.set("direction", state.sortOrder);
  if (state.page > 1) params.set("page", String(state.page));

  const value = params.toString();
  return value ? `?${value}` : "";
};

// The PO list, and the Order Queue (a PO opened from its Waiting on blank view
// goes back there). Matching the whole path keeps `/orders-x` and `//host` out.
const RETURN_TO_PATHS = ["/orders", "/production/orders"];

export const safePurchaseOrderListReturnTo = (value: unknown): string =>
  typeof value === "string" &&
  RETURN_TO_PATHS.some((path) => value === path || value.startsWith(`${path}?`))
    ? value
    : "/orders";
